import React, {useCallback} from "react";
import {useTranslation} from "react-i18next";
import {Participation, User} from "../../../schema";

import EditItemDropdown from "../../EditItemDropdown";
import {useModal} from "../../ModalProvider";
import ActivateParticipationModal from "./ActivateParticipationModal";

type props = {
  participation: Participation;
  onSetStatus?: (
    userId: string,
    status: boolean,
    challengeId?: string,
    projectId?: string,
    deactivateAt?: number,
    emails?: string[],
  ) => void;
  onParticipationDelete?: (userId: string, challengeId: string) => void;
  onParticipationReportExport?: (
    participation: Pick<Participation, "id" | "challenge">,
    user: Pick<User, "fullName" | "email">,
  ) => void;
};

export default function ParticipationEditDropdown({
  participation,
  onSetStatus,
  onParticipationDelete,
  onParticipationReportExport,
}: props) {
  const {t} = useTranslation();
  const {add: addModal} = useModal();

  const toggleStatus = useCallback(() => {
    if (!participation.isActive && participation.deactivateAt) {
      const modal = addModal({
        size: "m",
        header: <ActivateParticipationModal.Header />,
        content: (
          <ActivateParticipationModal.Content
            userId={participation.student.id}
            challengeId={participation.challenge.id}
            onSetStatus={onSetStatus}
            remove={() => modal.remove()}
          />
        ),
      });
    } else {
      onSetStatus && onSetStatus(participation.student.id, !participation.isActive, participation.challenge.id);
    }
  }, [addModal, onSetStatus, participation]);

  const deleteParticipation = useCallback(() => {
    onParticipationDelete && onParticipationDelete(participation.student.id, participation.challenge.id);
  }, [onParticipationDelete, participation]);

  const handleDownloadPdf = useCallback(() => {
    onParticipationReportExport && onParticipationReportExport(participation, participation.student);
  }, [onParticipationReportExport, participation]);

  return (
    <EditItemDropdown>
      <EditItemDropdown.Action
        title={
          participation.isActive
            ? t("components.ParticipationEditDropdown.deactivate")
            : t("components.ParticipationEditDropdown.activate")
        }
        onClick={toggleStatus}
      />
      <EditItemDropdown.Action
        title={t("components.ParticipationEditDropdown.delete")}
        onClick={deleteParticipation}
      />
      <EditItemDropdown.Action
        title={t("components.ParticipationEditDropdown.downloadPdf")}
        onClick={handleDownloadPdf}
        disabled={!onParticipationReportExport}
      />
    </EditItemDropdown>
  );
}
